import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow, ToolbarActionDef } from '@inavista/components/grid';
import { ACTION_ICON_SVG } from 'projects/service/src/app/modules/service-order/pages/main-page/service-order.grid.config';

// Leads grid (Sales leads module, shown in RIMS Service through service-app-leads-grid, and opened from the Sales
// dashboard's Leads pod) on @inavista/components. Rows: RIMS Sales API api/grid/leads (LeadsGridController), the rows of
// sls.spGetLeads for the user in the token, one per lead, paged in SQL. The dashboard's region, district and status are
// sent as query params, which can only narrow that list. Same columns, labels, actions, colours and permissions as the
// Syncfusion grid it replaces. Lead Type is not shown: no column holds it yet (ticket 14791).

export type LeadsGridMode = 'sales' | 'service' | 'dashboard';

export interface LeadsGridContext {
  saleApiUrl: string;
  mode: LeadsGridMode;
  // The dashboard's status ("All" = every status, then shown as a column), regions and districts (ids).
  dashboard?: { status?: string; regions?: string[]; districts?: string[] };
  // app.rolePermissions of the leadsGrid category; a rule that is missing counts as visible where the old grid did so.
  permissions: Record<string, { visible?: boolean; enable?: boolean }>;
  canActAsSalesRep: boolean;
  canExport: boolean;
  businessPartnerCell: TemplateRef<unknown>;
}

export const ADD_LEAD_ACTION = 'add-lead';

const BLACK = '#000000de';
const INACTIVE = '#cccccc';

const ICON = {
  map: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" />',
  convert: '<polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><polyline points="3 3 3 8 8 8" /><polyline points="12 7 12 12 15 14" />',
  add: '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />',
};

// Why Map Business Partner or Convert to Opportunity is not available on this row, or null when it is: the API's
// canMap / canConvert (sls.spGetLeads' rules). The old grid greyed them out without a reason.
export function leadActionDisabledReason(key: string, row: GridRow): string | null {
  if (key === 'convert' && !row['canConvert']) {
    if (row['isConverted']) return 'This lead has already been converted to an opportunity.';
    return 'Only a Qualified lead can be converted, by its sales rep, a manager over its territory or an admin.';
  }
  if (key === 'map' && !row['canMap']) {
    return 'Only an Awaiting Mapping or Unscheduled lead with a business partner can be mapped, by its sales rep, a manager over its territory or an admin.';
  }
  return null;
}

// The old command column, same order. Page: Map Business Partner, Convert to Opportunity, Details, Links, Comments,
// Attachments, History (Map, Convert and Links not in RIMS Service). Dashboard: Details, Links, Comments, Attachments.
export function buildLeadsActionIcons(ctx: LeadsGridContext): ActionIconDef[] {
  const visible = (rule: string, byDefault: boolean) => ctx.permissions[rule]?.visible ?? byDefault;
  const inService = ctx.mode === 'service';
  const gated = (key: string, label: string, icon: string, color: (row: GridRow) => string): ActionIconDef => ({
    key, label, icon,
    colorFn: row => (leadActionDisabledReason(key, row) ? INACTIVE : color(row)),
    disabledFn: row => !!leadActionDisabledReason(key, row),
    labelFn: row => leadActionDisabledReason(key, row) ?? label,
  });
  const details: ActionIconDef = { key: 'details', label: 'Details', icon: ACTION_ICON_SVG.details, colorFn: () => BLACK };
  const links: ActionIconDef = { key: 'links', label: 'Links', icon: ACTION_ICON_SVG.links, colorFn: row => (row['linkColorCode'] as string) || BLACK };
  const comments: ActionIconDef = { key: 'comments', label: 'Comments', icon: ACTION_ICON_SVG.comments, colorFn: row => (row['commentColor'] as string) || BLACK };
  const attachments: ActionIconDef = { key: 'attachments', label: 'Attachments', icon: ACTION_ICON_SVG.attachments, colorFn: row => (row['attColorCode'] as string) || BLACK };

  if (ctx.mode === 'dashboard') return [details, links, comments, attachments];
  return [
    ...(visible('leadsGrid_mapBusinessPartner', true) && !inService
      ? [gated('map', 'Map Business Partner', ICON.map, row => (row['mappedColorCode'] as string) || BLACK)] : []),
    ...((visible('leadsGrid_convertOpportunity', false) || ctx.canActAsSalesRep) && !inService
      ? [gated('convert', 'Convert to Opportunity', ICON.convert, () => BLACK)] : []),
    ...(visible('leadsGrid_details', true) ? [details] : []),
    ...(visible('leadsGrid_links', true) && !inService ? [links] : []),
    ...(visible('leadsGrid_comments', true) ? [comments] : []),
    attachments,
    ...(visible('leadsGrid_history', true) ? [{ key: 'history', label: 'History', icon: ICON.history, colorFn: () => BLACK }] : []),
  ];
}

export function buildLeadsToolbarActions(ctx: LeadsGridContext): ToolbarActionDef[] {
  return ctx.mode !== 'dashboard' && ctx.permissions['leadsGrid_add']?.visible
    ? [{ key: ADD_LEAD_ACTION, label: 'Add Lead', icon: ICON.add, kind: 'button', position: 'start' }]
    : [];
}

const PAGE_COLUMNS = [
  'salesLeadId', 'soldToBPName', 'assignedResourceName', 'salesRepName', 'statusText', 'soldToExtId', 'productCategory', 'zipCode',
  'stateProvince', 'country', 'telephone', 'creatorName', 'source', 'createdDate',
];
const DASHBOARD_COLUMNS = [
  'salesLeadId', 'soldToBPName', 'statusText', 'assignedResourceName', 'salesRepName', 'productCategory', 'zipCode', 'territory',
  'district', 'region', 'stateProvince', 'telephone', 'creatorName', 'source', 'createdDate',
];
// The endpoint's other columns: the actions, colours and popups read them, the old grid never showed them.
const BACKING_COLUMNS = [
  'salesRepId', 'assignedResourceId', 'statusColorCode', 'isMapped', 'canConvert', 'canMap', 'isConverted', 'mappedColorCode',
  'convertColorCode', 'creatorId', 'notes', 'reasonNotes', 'mappedBPName', 'mappedSoldToBP', 'mappedBPTelephone', 'mappedBPAddress',
  'commentColor', 'attColorCode', 'linkColorCode', 'mappedBPEmail', 'city', 'productLineValue', 'countryId',
];

const PAGE_LABELS: Record<string, string> = {
  salesLeadId: 'ID', soldToBPName: 'Business Partner', assignedResourceName: 'Assigned Resource', salesRepName: 'Primary Sales Rep',
  statusText: 'Status', soldToExtId: 'Business Partner ID', productCategory: 'Product', zipCode: 'Zip Code', stateProvince: 'State',
  country: 'Country', telephone: 'Telephone', creatorName: 'Created By', source: 'Source', createdDate: 'Created Date',
  territory: 'Territory', district: 'District', region: 'Region',
};
const DASHBOARD_LABELS: Record<string, string> = {
  ...PAGE_LABELS, salesLeadId: 'Lead ID', soldToBPName: 'Customer', createdDate: 'Creation Date',
};

const WIDTHS: Record<string, number> = {
  salesLeadId: 120, soldToBPName: 250, assignedResourceName: 200, salesRepName: 170, statusText: 170, soldToExtId: 150,
  productCategory: 220, zipCode: 100, stateProvince: 90, country: 120, telephone: 130, creatorName: 160, source: 140,
  createdDate: 130, territory: 120, district: 120, region: 120,
};
const CENTRED = ['salesLeadId', 'soldToExtId', 'zipCode', 'stateProvince'];

export function buildLeadsGridConfig(ctx: LeadsGridContext): GridConfig {
  const dashboard = ctx.mode === 'dashboard';
  // The dashboard shows Status only for "All" (the other statuses are the filter itself), as before.
  const showStatus = !dashboard || !ctx.dashboard?.status || ctx.dashboard.status === 'All';
  const columns = (dashboard ? DASHBOARD_COLUMNS : PAGE_COLUMNS).filter(c => c !== 'statusText' || showStatus);
  const notOffered = [
    ...BACKING_COLUMNS,
    ...(dashboard ? ['soldToExtId', 'country'] : ['territory', 'district', 'region']),
    ...(showStatus ? [] : ['statusText']),
  ];
  const list = (values?: string[]) => (values?.length ? values.join(',') : undefined);
  const queryParams: Record<string, string> = {};
  if (dashboard) {
    const region = list(ctx.dashboard?.regions);
    const district = list(ctx.dashboard?.districts);
    if (region) queryParams['region'] = region;
    if (district) queryParams['district'] = district;
    if (ctx.dashboard?.status && ctx.dashboard.status !== 'All') queryParams['statusText'] = ctx.dashboard.status;
  }
  return {
    apiBaseUrl: `${ctx.saleApiUrl}grid/leads`,
    queryParams,
    keyColumn: 'salesLeadId',
    pageSize: 200,
    rowHeight: 26,
    loadingIndicator: 'banner',
    wrapHeaders: true,
    enableSearch: true,
    enableRefresh: true,
    enableContextMenuFilter: true,
    enableFitColumns: true,
    enableSavedViews: true,
    enableResetLayout: true,
    // Excel export for every role but Sales Rep, as before: "Leads (<date>).xlsx".
    enableExport: ctx.canExport,
    defaultColumnOrder: columns,
    hiddenByDefaultColumns: notOffered,
    // Business Partner ID and Country start hidden on the page, as before.
    startHiddenColumns: dashboard ? [] : ['soldToExtId', 'country'],
    // The procedure's order: newest lead first.
    defaultSort: { column: 'createdDate', direction: 'desc' },
    columnLabels: dashboard ? DASHBOARD_LABELS : PAGE_LABELS,
    columnWidths: WIDTHS,
    // RIMS grid look: headers left; text and dates left; codes and IDs centred.
    columnAlign: Object.fromEntries(columns.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(CENTRED.includes(c) ? { horizontal: 'center' as const } : {}),
    }])),
    columnTypes: { createdDate: 'date' },
    columnDateFormats: { createdDate: 'd MMM, y' },
    columnColors: { statusText: (_value, row) => String(row['statusColorCode'] ?? '') },
    // The dashboard shows "<Business Partner ID> - <name>" as text (just the name when there is no ID: the old grid
    // showed "- <name>"); the page links the name to the business partner when the lead has one.
    ...(dashboard
      ? { columnFormatters: { soldToBPName: (value: unknown, row: GridRow) =>
          row['soldToExtId'] ? `${row['soldToExtId']} - ${value ?? ''}` : String(value ?? '') } }
      : { cellTemplates: { soldToBPName: ctx.businessPartnerCell } }),
    // The lead ID opens the lead.
    hyperlinkColumns: ['salesLeadId'],
    // Checkbox filters on the columns with few values (the old grid also had them on zip code and telephone, lists of
    // thousands of single values: those keep the text filter).
    excelFilterColumns: ['assignedResourceName', 'salesRepName', 'statusText', 'productCategory', 'stateProvince', 'country',
      'creatorName', 'source', 'territory', 'district', 'region'],
    emptyStateMessage: dashboard
      ? 'No leads to show for this status, region and district.'
      : 'No leads to show. The list shows the leads your role and territories give you access to.',
    // The dashboard keeps two layouts: "All" (with Status) and a single status (without it), so neither hides the other's.
    persistenceKey: ctx.mode === 'service' ? 'rims.leads.list-service'
      : !dashboard ? 'rims.leads.list-sales'
      : showStatus ? 'rims.leads.dashboard' : 'rims.leads.dashboard-status',
    persistenceVersion: 1,
  };
}
